/**
 * UnifiedMapView.tsx
 * Componente unificado de mapas baseado em React-Leaflet.
 * Suporta toolbar flutuante com filtros, contadores e alternância de clusterização.
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap, Circle, CircleMarker, Rectangle, useMapEvents } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-cluster';
import { disperseStackedMapPoints } from './disperseStackedMapPoints';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Corrigir referências de imagens padrão do Leaflet no Vite
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl:       'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl:     'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Estilos de Marcadores customizados premium e coloridos
const icons: Record<string, L.DivIcon> = {
  PREFEITURA: L.divIcon({
    className: '',
    html: `<div style="
      width:36px;height:36px;
      background:linear-gradient(135deg,#fbbf24,#f59e0b);
      border:3px solid #fff;
      border-radius:50%;
      display:flex;align-items:center;justify-content:center;
      font-size:18px;
      box-shadow:0 3px 12px rgba(245,158,11,0.6);
    ">🏛️</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  }),
  SCHOOL: L.divIcon({
    className: '',
    html: `<div style="
      width:44px;height:44px;
      background:linear-gradient(135deg,#7c3aed,#a855f7);
      border:3px solid #fff;
      border-radius:50%;
      display:flex;align-items:center;justify-content:center;
      font-size:22px;
      box-shadow:0 4px 14px rgba(124,58,237,0.55);
    ">🏫</div>`,
    iconSize: [44, 44],
    iconAnchor: [22, 22],
  }),
  STUDENT_HOME: L.divIcon({
    className: '',
    html: `<div style="
      width:24px;height:24px;
      background:#3b82f6;
      border:2.5px solid #fff;
      border-radius:50%;
      display:flex;align-items:center;justify-content:center;
      font-size:12px;
      box-shadow:0 2px 6px rgba(59,130,246,0.5);
    ">🏠</div>`,
    iconSize: [24, 24],
    iconAnchor: [12, 12],
  }),
  STUDENT: L.divIcon({
    className: '',
    html: `<div style="
      width:10px;height:10px;
      background:#10b981;
      border:1.5px solid #fff;
      border-radius:50%;
      box-shadow:0 1px 4px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [10, 10],
    iconAnchor: [5, 5],
  }),
  GUARDIAN: L.divIcon({
    className: '',
    html: `<div style="
      width:10px;height:10px;
      background:#f59e0b;
      border:1.5px solid #fff;
      border-radius:50%;
      box-shadow:0 1px 4px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [10, 10],
    iconAnchor: [5, 5],
  }),
  VEHICLE: L.divIcon({
    className: '',
    html: `<div style="
      width:28px;height:28px;
      background:#eab308;
      border:2px solid #fff;
      border-radius:50%;
      display:flex;align-items:center;justify-content:center;
      font-size:14px;
      box-shadow:0 2px 6px rgba(234,179,8,0.5);
    ">🚌</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  }),
  LEITOR_RFID: L.divIcon({
    className: '',
    html: `<div style="
      width:28px;height:28px;
      background:#ec4899;
      border:2px solid #fff;
      border-radius:50%;
      display:flex;align-items:center;justify-content:center;
      font-size:14px;
      box-shadow:0 2px 6px rgba(236,72,153,0.5);
    ">📡</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  }),
};

// Componente utilitário para centralizar/recentrar o mapa (só quando NÃO há pontos para fitBounds)
function MapRecenter({ center, enabled = false }: { center: [number, number]; enabled?: boolean }) {
  const map = useMap();
  const lat = center[0];
  const lng = center[1];
  useEffect(() => {
    if (!enabled) return;
    if (!isFinite(lat) || !isFinite(lng)) return;
    const t = setTimeout(() => {
      try {
        if (map && map.getSize().x > 0) {
          map.flyTo([lat, lng], 13, { duration: 1.0 });
        } else {
          map.setView([lat, lng], 13);
        }
      } catch {
        // Ignora falhas de inicialização em grids ocultos
      }
    }, 100);
    return () => clearTimeout(t);
    // Tamanho fixo do array de deps (evita warning do React no HMR)
  }, [lat, lng, map, enabled]);
  return null;
}

// Componente utilitário para recalcular tamanho do Leaflet quando a div de estilo for redimensionada
function MapResizeObserver({ height }: { height?: number | string }) {
  const map = useMap();
  useEffect(() => {
    if (!map) return;

    const invalidate = () => {
      try {
        map.invalidateSize();
      } catch {
        // Ignora falhas silenciosas
      }
    };

    const t = setTimeout(invalidate, 50);
    const t2 = setTimeout(invalidate, 300);

    const container = map.getContainer();
    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => invalidate())
        : null;
    if (container && ro) ro.observe(container);

    return () => {
      clearTimeout(t);
      clearTimeout(t2);
      ro?.disconnect();
    };
  }, [height, map]);
  return null;
}

export interface MapDataPoint {
  id: string;
  type: 'SCHOOL' | 'STUDENT_HOME' | 'VEHICLE' | 'LEITOR_RFID' | 'STUDENT' | 'GUARDIAN' | 'PREFEITURA';
  lat: number;
  lng: number;
  label: string;
  sublabel?: string;
  payload?: any;
}

export interface UnifiedMapConfig {
  initialZoom?: number;
  useClusteringByDefault?: boolean;
  showRadiusByDefault?: boolean;
  enableToolbar?: boolean;
  theme?: 'dark' | 'light';
}

export interface UnifiedMapViewProps {
  dataPoints: MapDataPoint[];
  config?: UnifiedMapConfig;
  className?: string;
  style?: React.CSSProperties;
  onMarkerClick?: (point: MapDataPoint) => void; // Dispara quando clica num marcador
}

/** Enquadra todos os marcadores na área útil (margem curta), reaplicando após resize. */
function MapInitBounds({
  points,
  rightPanelPad = 24,
}: {
  points: MapDataPoint[];
  rightPanelPad?: number;
}) {
  const map = useMap();

  const fitAll = React.useCallback(() => {
    const validPoints = points.filter(p => p.lat && p.lng && isFinite(p.lat) && isFinite(p.lng));
    if (validPoints.length === 0) return;
    try {
      if (map.getSize().x <= 0 || map.getSize().y <= 0) {
        map.invalidateSize();
      }
      const bounds = L.latLngBounds(validPoints.map(p => [p.lat, p.lng] as [number, number]));
      // Contraí ~24% (dobro do ajuste anterior) para chegar bem mais perto dos marcadores
      const tight = bounds.pad(-0.24);
      map.fitBounds(tight.isValid() ? tight : bounds, {
        paddingTopLeft: L.point(8, 10),
        paddingBottomRight: L.point(Math.max(8, rightPanelPad), 10),
        maxZoom: 19,
        animate: false,
      });
    } catch {
      // noop
    }
  }, [map, points, rightPanelPad]);

  useEffect(() => {
    fitAll();
    const t1 = setTimeout(fitAll, 80);
    const t2 = setTimeout(fitAll, 350);

    const container = map.getContainer();
    let raf = 0;
    const ro =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
              try {
                map.invalidateSize();
              } catch {
                // noop
              }
              fitAll();
            });
          })
        : null;
    if (container && ro) ro.observe(container);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      cancelAnimationFrame(raf);
      ro?.disconnect();
    };
  }, [fitAll, map]);

  return null;
}

export function UnifiedMapView({ dataPoints, config, className = '', style, onMarkerClick }: UnifiedMapViewProps) {
  const theme = config?.theme ?? 'light'; // Padrão Branco Claro
  const isLight = theme === 'light';

  const mapRef = React.useRef<L.Map | null>(null);

  // Configurações iniciais
  const enableToolbar = config?.enableToolbar ?? true;
  const initialZoom = config?.initialZoom ?? 13;

  // Estado dos filtros, raio de cobertura e clusterização
  const [isClustered, setIsClustered] = useState(() => config?.useClusteringByDefault ?? true);
  const [showRadius, setShowRadius] = useState(() => config?.showRadiusByDefault ?? true);
  
  // Estados adicionais premium
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [selectedFamily, setSelectedFamily] = useState<any>(null);
  const [selectedFamilyHomeId, setSelectedFamilyHomeId] = useState<string | null>(null);
  const [subFilter, setSubFilter] = useState<'all' | 'app' | 'rfid'>('all');
  
  // Dispersão única: wizard (EscolaStep) e OverviewPanel / qualquer consumidor
  const resolvedPoints = useMemo(
    () => disperseStackedMapPoints(dataPoints),
    [dataPoints],
  );

  // Obter tipos únicos presentes na lista de pontos de entrada
  const availableTypes = useMemo(() => {
    const types = new Set<string>();
    resolvedPoints.forEach(p => types.add(p.type));
    return Array.from(types);
  }, [resolvedPoints]);

  const [visibleTypes, setVisibleTypes] = useState<Set<string>>(() => new Set(['PREFEITURA', 'SCHOOL', 'STUDENT_HOME', 'STUDENT', 'GUARDIAN', 'VEHICLE']));

  // Sincroniza tipos visíveis quando a lista de pontos muda
  useEffect(() => {
    setVisibleTypes(new Set(['PREFEITURA', 'SCHOOL', 'STUDENT_HOME', 'STUDENT', 'GUARDIAN', 'VEHICLE']));
  }, [resolvedPoints]);

  // Filtra os pontos visíveis no mapa (visibilidade da camada e filtros especiais de Apresentação)
  const filteredPoints = useMemo(() => {
    return resolvedPoints.filter(p => {
      if (!visibleTypes.has(p.type)) return false;
      if (subFilter === 'app' && p.type === 'GUARDIAN' && p.payload?.temApp === false) return false;
      if (subFilter === 'rfid' && p.type === 'STUDENT' && p.payload?.temRfid === false) return false;
      return true;
    });
  }, [resolvedPoints, visibleTypes, subFilter]);

  // Contagem de pontos por tipo
  const counts = useMemo(() => {
    const map: Record<string, number> = {};
    filteredPoints.forEach(p => {
      map[p.type] = (map[p.type] ?? 0) + 1;
    });
    return map;
  }, [filteredPoints]);

  // Escolas mapeadas para renderizar o raio de cobertura
  const schools = useMemo(() => {
    return filteredPoints.filter(p => p.type === 'SCHOOL');
  }, [filteredPoints]);

  // Centro do mapa (coordenada média dos pontos de tipo SCHOOL ou o primeiro ponto disponível)
  const mapCenter = useMemo((): [number, number] => {
    const schools = resolvedPoints.filter(p => p.type === 'SCHOOL');
    if (schools.length > 0) {
      return [schools[0].lat, schools[0].lng];
    }
    if (resolvedPoints.length > 0) {
      return [resolvedPoints[0].lat, resolvedPoints[0].lng];
    }
    return [-20.0754, -44.5775]; // Itaúna fallback
  }, [resolvedPoints]);

  const toggleType = useCallback((type: string) => {
    setVisibleTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  }, []);

  const hasCoords = resolvedPoints.length > 0 && isFinite(mapCenter[0]) && isFinite(mapCenter[1]) && mapCenter[0] !== 0 && mapCenter[1] !== 0;

  if (!hasCoords) {
    return (
      <div className={`flex items-center justify-center rounded-xl border ${isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/5 border-white/10'} ${className}`} style={style}>
        <div className={`text-center text-xs opacity-60 ${isLight ? 'text-slate-400' : 'text-[#a6adc8]'}`}>
          <div className="text-2xl mb-1">📍</div>
          <span>Sem coordenadas válidas</span>
        </div>
      </div>
    );
  }

  const handleMarkerSelection = useCallback((p: MapDataPoint) => {
    const familia = p.payload?.familia;
    if (familia && (p.type === 'STUDENT_HOME' || p.type === 'STUDENT' || p.type === 'GUARDIAN')) {
      setSelectedFamily(familia);
      setSelectedFamilyHomeId(familia.idHome);

      const familyPoints = resolvedPoints.filter(dp => dp.payload?.familia?.idHome === familia.idHome);
      if (familyPoints.length > 0 && mapRef.current) {
        const bounds = L.latLngBounds(familyPoints.map(fp => [fp.lat, fp.lng]));
        mapRef.current.fitBounds(bounds, {
          maxZoom: 18,
          paddingTopLeft: L.point(30, 30),
          paddingBottomRight: L.point(enableToolbar && !isCollapsed ? 290 : 30, 30),
          animate: true,
        });
      }
    } else {
      setSelectedFamily(null);
      setSelectedFamilyHomeId(null);
    }

    if (onMarkerClick) {
      onMarkerClick(p);
    }
  }, [resolvedPoints, onMarkerClick, enableToolbar, isCollapsed]);

  // Renderizadores de marcadores segregados para evitar agrupamento misto (Escola com Aluno)
  const renderSchoolMarkers = () => {
    return filteredPoints
      .filter(p => p.type === 'SCHOOL' || p.type === 'PREFEITURA')
      .map(p => (
        <Marker 
          key={p.id} 
          position={[p.lat, p.lng]} 
          icon={icons[p.type] ?? icons.SCHOOL}
          eventHandlers={{
            click: () => {
              handleMarkerSelection(p);
            }
          }}
        >
          <Popup className={isLight ? '' : 'leaflet-dark-popup'} autoClose={true} closeOnClick={true}>
            <div style={{ fontFamily: 'sans-serif', fontSize: '11px', fontWeight: 'bold', color: '#0f0a23', lineHeight: 'normal' }}>{p.label}</div>
            {p.sublabel && <div style={{ fontFamily: 'sans-serif', fontSize: '9px', color: '#4b5563', marginTop: '2px' }}>{p.sublabel}</div>}
          </Popup>
        </Marker>
      ));
  };

  // Renderiza residências como quadrados cinzas sem borda (raio de 15 metros)
  const renderResidences = () => {
    return filteredPoints
      .filter(p => p.type === 'STUDENT_HOME')
      .map(p => {
        const latOffset = 15 / 111000;
        const lngOffset = 15 / (111000 * Math.cos(p.lat * Math.PI / 180));
        const bounds: [number, number][] = [
          [p.lat - latOffset, p.lng - lngOffset],
          [p.lat + latOffset, p.lng + lngOffset]
        ];

        const isSelected = selectedFamilyHomeId === p.id;

        return (
          <Rectangle
            key={p.id}
            bounds={bounds as any}
            pathOptions={{
              fillColor: isSelected ? '#ef4444' : '#64748b', // Vermelho pulsante se selecionada, cinza se comum
              fillOpacity: isSelected ? 0.35 : 0.18,
              stroke: isSelected,
              color: '#ef4444',
              weight: isSelected ? 3 : 0,
              className: isSelected ? 'map-pulse-node-rect' : undefined,
              interactive: false // Otimização Canvas
            }}
          />
        );
      });
  };

  const renderOtherMarkers = () => {
    return filteredPoints
      .filter(p => p.type !== 'SCHOOL' && p.type !== 'PREFEITURA' && p.type !== 'STUDENT_HOME')
      .map(p => {
        // Alunos e Responsáveis se beneficiam de CircleMarker renderizados via Canvas
        if (p.type === 'STUDENT' || p.type === 'GUARDIAN') {
          const isStudent = p.type === 'STUDENT';
          const isSelectedFamilyMember = selectedFamilyHomeId && p.payload?.familia?.idHome === selectedFamilyHomeId;

          return (
            <CircleMarker
              key={p.id}
              center={[p.lat, p.lng]}
              radius={isSelectedFamilyMember ? 7.5 : 5}
              pathOptions={{
                fillColor: isSelectedFamilyMember ? '#ef4444' : (isStudent ? '#10b981' : '#f59e0b'),
                fillOpacity: 0.95,
                color: '#ffffff',
                weight: isSelectedFamilyMember ? 2.5 : 1.5,
                className: isSelectedFamilyMember ? 'map-pulse-node' : undefined
              }}
              eventHandlers={{
                click: () => {
                  handleMarkerSelection(p);
                }
              }}
            >
              <Popup className={isLight ? '' : 'leaflet-dark-popup'} autoClose={true} closeOnClick={true}>
                <div style={{ fontFamily: 'sans-serif', fontSize: '11px', fontWeight: 'bold', color: '#0f0a23', lineHeight: 'normal' }}>{p.label}</div>
                {p.sublabel && <div style={{ fontFamily: 'sans-serif', fontSize: '9px', color: '#4b5563', marginTop: '2px' }}>{p.sublabel}</div>}
              </Popup>
            </CircleMarker>
          );
        }

        // Outros pontos (ex: veículos, RFID) usam os marcadores padrão com ícones
        return (
          <Marker 
            key={p.id} 
            position={[p.lat, p.lng]} 
            icon={icons[p.type] ?? icons.SCHOOL}
            eventHandlers={{
              click: () => {
                handleMarkerSelection(p);
              }
            }}
          >
            <Popup className={isLight ? '' : 'leaflet-dark-popup'} autoClose={true} closeOnClick={true}>
              <div style={{ fontFamily: 'sans-serif', fontSize: '11px', fontWeight: 'bold', color: '#0f0a23', lineHeight: 'normal' }}>{p.label}</div>
              {p.sublabel && <div style={{ fontFamily: 'sans-serif', fontSize: '9px', color: '#4b5563', marginTop: '2px' }}>{p.sublabel}</div>}
            </Popup>
          </Marker>
        );
      });
  };

  // Nomes amigáveis dos filtros de tipos (sempre em maiúscula)
  const typeLabels: Record<string, { label: string; emoji: string; badgeClass: string; visibleClass: string }> = {
    PREFEITURA: { 
      label: 'PREFEITURA', 
      emoji: '🏛️', 
      badgeClass: 'bg-amber-100 text-amber-800 border-amber-200',
      visibleClass: 'bg-amber-50 border-amber-300 text-amber-800 font-extrabold shadow-sm'
    },
    SCHOOL: { 
      label: 'ESCOLAS', 
      emoji: '🏫', 
      badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-200', // Verde como solicitado
      visibleClass: 'bg-emerald-50 border-emerald-300 text-emerald-800 font-extrabold shadow-sm'
    },
    STUDENT_HOME: { 
      label: 'RESIDÊNCIAS', 
      emoji: '🏠', 
      badgeClass: 'bg-slate-100 text-slate-600 border-slate-200',
      visibleClass: 'bg-slate-50 border-slate-300 text-slate-700 font-extrabold shadow-sm'
    },
    STUDENT: { 
      label: 'ALUNOS', 
      emoji: '👥', 
      badgeClass: 'bg-emerald-100 text-emerald-800 border-emerald-200',
      visibleClass: 'bg-emerald-50 border-emerald-300 text-emerald-800 font-extrabold shadow-sm'
    },
    GUARDIAN: { 
      label: 'RESPONSÁVEIS', 
      emoji: '👤', 
      badgeClass: 'bg-amber-100 text-amber-800 border-amber-200',
      visibleClass: 'bg-amber-50 border-amber-300 text-amber-800 font-extrabold shadow-sm'
    },
    VEHICLE: { 
      label: 'VEÍCULOS', 
      emoji: '🚌', 
      badgeClass: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      visibleClass: 'bg-yellow-50 border-yellow-300 text-yellow-800 font-extrabold shadow-sm'
    },
    LEITOR_RFID: { 
      label: 'LEITORES RFID', 
      emoji: '📡', 
      badgeClass: 'bg-pink-100 text-pink-800 border-pink-200',
      visibleClass: 'bg-pink-50 border-pink-300 text-pink-800 font-extrabold shadow-sm'
    },
  };

  // Componente interno para fechar card no clique do mapa
  function MapEventsHandler() {
    useMapEvents({
      click: () => {
        setSelectedFamily(null);
        setSelectedFamilyHomeId(null);
      }
    });
    return null;
  }

  return (
    <div className={`rounded-xl overflow-hidden border relative flex-1 ${
      isMaximized 
        ? 'fixed inset-0 w-screen h-screen z-[2000] !rounded-none' 
        : (isLight ? 'border-slate-200 shadow-sm' : 'border-white/8')
    }`}
         style={{ isolation: 'isolate', minHeight: 200, ...style }}>
      
      {/* ── TOOLBAR FLUTUANTE ─────────────────────────────────────────────────── */}
      {enableToolbar && (
        <div className={`absolute top-3 right-3 z-[1000] rounded-xl border backdrop-blur-md p-2.5 flex flex-col gap-2 shadow-lg transition-all ${
          isCollapsed ? 'w-[145px] max-h-[38px] overflow-hidden' : 'w-[185px]'
        } ${
          isLight ? 'bg-white/92 border-slate-200/80 text-slate-800' : 'bg-[#181825]/92 border-white/8 text-white'
        }`}
        style={{ transitionDuration: '250ms' }}>
          
          {/* Header da Toolbar */}
          <div 
            onClick={() => setIsCollapsed(c => !c)}
            onDoubleClick={() => setIsMaximized(m => !m)}
            className="text-[9.5px] font-extrabold uppercase tracking-widest border-b pb-1.5 border-current flex items-center justify-between cursor-pointer select-none" 
            style={{ fontFamily: 'sans-serif' }}
            title="Clique para colapsar, Duplo-clique para tela cheia"
          >
            <span className="flex items-center gap-1">
              <span>{isCollapsed ? '▼' : '▲'}</span>
              <span>VISUALIZAÇÃO</span>
            </span>
            <button 
              onClick={(e) => { e.stopPropagation(); setIsMaximized(m => !m); }}
              className="text-[8px] font-bold tracking-tight opacity-75 hover:opacity-100 transition-opacity"
            >
              {isMaximized ? '🗗 RESTAURAR' : '🗖 TELA CHEIA'}
            </button>
          </div>

          {!isCollapsed && (
            <>
              {/* Opções de camadas (Toggles) */}
              <div className="flex flex-col gap-1.5 animate-in fade-in duration-200">
                {availableTypes.map(type => {
                  const info = typeLabels[type] ?? { label: type, emoji: '📍', badgeClass: 'bg-blue-100 text-blue-800 border-blue-200', visibleClass: 'bg-blue-50 border-blue-300' };
                  const isVisible = visibleTypes.has(type);
                  const count = counts[type] ?? 0;
                  return (
                    <button
                      key={type}
                      onClick={() => toggleType(type)}
                      className={`flex items-center justify-between text-left text-[9px] font-bold py-1 px-1.5 rounded-lg border transition-all cursor-pointer ${
                        isVisible
                          ? info.visibleClass
                          : 'bg-transparent border-transparent text-[#6c7086] hover:bg-black/5'
                      }`}
                      style={{ fontFamily: 'sans-serif' }}
                    >
                      <span className="truncate flex items-center gap-1">
                        <span>{info.emoji}</span>
                        <span>{info.label}</span>
                      </span>
                      <span className={`text-[8.5px] px-1.5 py-0.5 rounded-full font-extrabold border ${info.badgeClass}`}>{count}</span>
                    </button>
                  );
                })}
              </div>

              {/* Subfiltro de Apresentação (Radio) */}
              <div className="border-t pt-2 mt-1 border-slate-200/50 flex flex-col gap-1.5 animate-in fade-in duration-200 delay-100">
                <span className="text-[8px] font-extrabold text-slate-400 tracking-wider uppercase">Filtros Avançados</span>
                <div className="flex flex-col gap-1.5">
                  {[
                    { value: 'all', label: 'MOSTRAR TODOS' },
                    { value: 'app', label: 'RESPONSÁVEIS COM APP' },
                    { value: 'rfid', label: 'ALUNOS COM RFID' }
                  ].map(opt => (
                    <label key={opt.value} className="flex items-center gap-2 text-[8.5px] font-bold cursor-pointer text-slate-600 hover:text-slate-900 select-none">
                      <input
                        type="radio"
                        name="subFilter"
                        value={opt.value}
                        checked={subFilter === opt.value}
                        onChange={() => setSubFilter(opt.value as any)}
                        className="w-2.5 h-2.5 text-blue-600 border-slate-300 focus:ring-blue-500 cursor-pointer"
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Toggle de clusterização */}
              <button
                onClick={() => setIsClustered(c => !c)}
                className={`w-full mt-1.5 py-1 px-1.5 rounded-lg border text-center text-[9px] font-extrabold uppercase transition-all cursor-pointer ${
                  isClustered
                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                    : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'
                }`}
                style={{ fontFamily: 'sans-serif' }}
              >
                {isClustered ? '⚡ CLUSTER ATIVO' : '⚪ AGRUPAR PINS'}
              </button>

              {/* Toggle de raio de cobertura */}
              {schools.length > 0 && (
                <button
                  onClick={() => setShowRadius(r => !r)}
                  className={`w-full mt-1 py-1 px-1.5 rounded-lg border text-center text-[9px] font-extrabold uppercase transition-all cursor-pointer ${
                    showRadius
                      ? 'bg-violet-50 border-violet-200 text-violet-700'
                      : 'bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100'
                  }`}
                  style={{ fontFamily: 'sans-serif' }}
                >
                  {showRadius ? '⭕ RAIO 1.5KM ATIVO' : '⚪ EXIBIR RAIO ESCOLAR'}
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* ── CARD DA FAMÍLIA ANIMADO (ESQUERDA) ─────────────────────────────────── */}
      {selectedFamily && (
        <div 
          className={`absolute top-3 left-3 bottom-3 z-[1000] w-[330px] rounded-2xl border backdrop-blur-md p-4 flex flex-col shadow-2xl transition-all duration-300 ease-out animate-in slide-in-from-left ${
            isLight ? 'bg-white/95 border-slate-200/90 text-slate-800' : 'bg-[#181825]/95 border-white/10 text-white'
          }`}
          style={{ fontFamily: 'sans-serif' }}
        >
          {/* Header do Card */}
          <div className="flex items-center justify-between border-b pb-2.5 mb-3 border-slate-200/60 opacity-90">
            <span className="text-[10px] font-extrabold tracking-widest uppercase text-slate-500">NÚCLEO FAMILIAR</span>
            <button 
              onClick={() => { setSelectedFamily(null); setSelectedFamilyHomeId(null); }}
              className="text-xs font-bold hover:scale-110 cursor-pointer transition-transform text-slate-400 hover:text-slate-700"
            >
              ✕
            </button>
          </div>

          {/* Conteúdo com animação suave de fade-in */}
          <div className="flex-1 overflow-y-auto pr-1 flex flex-col gap-4 animate-in fade-in-50 duration-500">
            
            {/* Bloco Bairro */}
            <div className={`py-1.5 px-3 rounded-lg text-center font-extrabold text-[10px] tracking-wider ${
              isLight ? 'bg-slate-100 text-slate-700' : 'bg-white/5 text-slate-300'
            }`}>
              BAIRRO: {selectedFamily.bairro.toUpperCase()}
            </div>

            {/* Responsável Principal */}
            <div className="flex flex-col gap-2 border-b pb-3 border-slate-100">
              <span className="text-[8px] font-bold text-slate-400 tracking-wider">RESPONSÁVEL PRINCIPAL</span>
              <div className="flex items-center gap-3">
                <img 
                  src={selectedFamily.responsavel1.avatar} 
                  alt="Avatar" 
                  className="w-11 h-11 rounded-full border border-violet-300 shadow-md"
                />
                <div className="flex-1">
                  <div className="text-xs font-extrabold text-slate-900 leading-tight uppercase">
                    {selectedFamily.responsavel1.nome.toUpperCase()}
                  </div>
                  <div className="text-[9px] font-semibold text-slate-500 mt-0.5">
                    Responsável Legal
                  </div>
                </div>
              </div>
              {selectedFamily.responsavel1.temApp ? (
                <div className="flex items-center gap-1.5 mt-1.5 px-2.5 py-1 rounded-lg bg-emerald-100/90 text-emerald-800 border border-emerald-200 text-[9px] font-extrabold tracking-wider w-max">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping"></span>
                  📲 APP INSTALADO
                </div>
              ) : (
                <div className="flex items-center gap-1.5 mt-1.5 px-2.5 py-1 rounded-lg bg-slate-100 text-slate-500 border border-slate-200 text-[9px] font-semibold tracking-wider w-max">
                  📴 APP NÃO INSTALADO
                </div>
              )}
            </div>

            {/* Responsável Secundário (se houver) */}
            {selectedFamily.responsavel2 && (
              <div className="flex flex-col gap-2 border-b pb-3 border-slate-100">
                <span className="text-[8px] font-bold text-slate-400 tracking-wider">RESPONSÁVEL ADICIONAL</span>
                <div className="flex items-center gap-3">
                  <img 
                    src={selectedFamily.responsavel2.avatar} 
                    alt="Avatar" 
                    className="w-11 h-11 rounded-full border border-blue-300 shadow-md"
                  />
                  <div className="flex-1">
                    <div className="text-xs font-extrabold text-slate-900 leading-tight uppercase">
                      {selectedFamily.responsavel2.nome.toUpperCase()}
                    </div>
                    <div className="text-[9px] font-semibold text-slate-500 mt-0.5">
                      Responsável Secundário
                    </div>
                  </div>
                </div>
                {selectedFamily.responsavel2.temApp ? (
                  <div className="flex items-center gap-1.5 mt-1.5 px-2.5 py-1 rounded-lg bg-emerald-100/90 text-emerald-800 border border-emerald-200 text-[9px] font-extrabold tracking-wider w-max">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-ping"></span>
                    📲 APP INSTALADO
                  </div>
                ) : (
                  <div className="flex items-center gap-1.5 mt-1.5 px-2.5 py-1 rounded-lg bg-slate-100 text-slate-500 border border-slate-200 text-[9px] font-semibold tracking-wider w-max">
                    📴 APP NÃO INSTALADO
                  </div>
                )}
              </div>
            )}

            {/* Alunos Vinculados */}
            <div className="flex flex-col gap-2">
              <span className="text-[8px] font-bold text-slate-400 tracking-wider">ALUNOS VINCULADOS</span>
              <div className="flex flex-col gap-3">
                <div className={`p-3 rounded-xl border flex flex-col gap-2 ${
                  isLight ? 'bg-slate-50/50 border-slate-150' : 'bg-white/5 border-white/5'
                }`}>
                  <div className="flex items-center gap-2.5">
                    <img 
                      src={selectedFamily.aluno.avatar} 
                      alt="Avatar Aluno" 
                      className="w-10 h-10 rounded-full border border-emerald-300 shadow-sm"
                    />
                    <div className="flex-1">
                      <div className="text-xs font-extrabold text-slate-900 uppercase">
                        {selectedFamily.aluno.nome.toUpperCase()}
                      </div>
                      <div className="text-[9px] font-semibold text-slate-500 mt-0.5 uppercase">
                        TURMA: {selectedFamily.turma}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-1.5 mt-1 text-[8.5px]">
                    <div className="font-semibold text-slate-500">
                      RFID: <span className={selectedFamily.aluno.temRfid ? 'text-emerald-600 font-extrabold' : 'text-slate-400'}>
                        {selectedFamily.aluno.temRfid ? 'ATIVO' : 'INATIVO'}
                      </span>
                    </div>
                    <div className="font-semibold text-slate-500 text-right">
                      ESCOLA: <span className="text-violet-600 font-extrabold truncate block max-w-[80px] float-right">{selectedFamily.escolaNome.toUpperCase()}</span>
                    </div>
                  </div>

                  {/* Professores */}
                  <div className="border-t pt-2 mt-1.5 border-slate-200/50">
                    <span className="text-[7.5px] font-bold text-slate-400 block tracking-wider uppercase">CORPO DOCENTE</span>
                    <div className="text-[8.5px] font-bold text-slate-700 mt-0.5 uppercase leading-snug">
                      {selectedFamily.professores.join(' E ')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>
      )}

      <style>{`
        .leaflet-tile {
          filter: grayscale(1) brightness(0.92) contrast(1.08) !important;
        }
        .leaflet-container {
          background: ${isLight ? '#f1f5f9' : '#0f172a'} !important;
        }
        @keyframes pulse-rect {
          0% { fill-opacity: 0.18; stroke-opacity: 0.5; stroke-width: 2px; }
          50% { fill-opacity: 0.45; stroke-opacity: 1; stroke-width: 5px; }
          100% { fill-opacity: 0.18; stroke-opacity: 0.5; stroke-width: 2px; }
        }
        @keyframes pulse-circle {
          0% { stroke-width: 1.5px; stroke-opacity: 0.5; }
          50% { stroke-width: 3.5px; stroke-opacity: 1; }
          100% { stroke-width: 1.5px; stroke-opacity: 0.5; }
        }
        .map-pulse-node-rect {
          animation: pulse-rect 1.2s infinite ease-in-out !important;
          stroke: #ef4444 !important;
          fill: #ef4444 !important;
        }
        .map-pulse-node {
          animation: pulse-circle 1s infinite ease-in-out !important;
          stroke: #ef4444 !important;
          fill: #ef4444 !important;
        }
      `}</style>

      {/* ── MOTOR MAPCONTAINER LEAFLET ────────────────────────────────────────── */}
      <MapContainer
        preferCanvas={true}
        center={mapCenter}
        zoom={initialZoom}
        scrollWheelZoom
        style={{ width: '100%', height: '100%', background: isLight ? '#f8fafc' : '#1e1e2e' }}
        zoomControl={false}
        ref={mapRef as any}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
          url={isLight
            ? "https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
            : "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          }
        />

        <MapRecenter center={mapCenter} enabled={resolvedPoints.length === 0} />
        <MapResizeObserver height={style?.height} />
        <MapInitBounds
          points={filteredPoints.length > 0 ? filteredPoints : resolvedPoints}
          rightPanelPad={enableToolbar && !isCollapsed ? 260 : 12}
        />
        <MapEventsHandler />

        {/* Círculos de Raio de Cobertura da Escola */}
        {showRadius && schools.map(s => (
          <Circle
            key={`circle-${s.id}`}
            center={[s.lat, s.lng]}
            radius={1500}
            pathOptions={{
              color: '#7c3aed',
              fillColor: '#7c3aed',
              fillOpacity: 0.04,
              weight: 1.2,
              dashArray: '5, 8'
            }}
          />
        ))}

        {/* Círculos de Residências Translúcidos (no fundo) */}
        {renderResidences()}

        {/* Escolas e Prefeitura — sempre individuais (sem cluster) */}
        {renderSchoolMarkers()}

        {/* Demais pontos (residências, alunos, responsáveis) — cluster opcional */}
        {isClustered ? (
          <MarkerClusterGroup
            chunkedLoading
            maxClusterRadius={45}
            showCoverageOnHover={false}
            iconCreateFunction={(cluster: any) => {
              const count = cluster.getChildCount();
              return L.divIcon({
                className: '',
                html: `<div style="
                  width:32px;height:32px;
                  background:rgba(59,130,246,0.9);
                  border:2px solid rgba(255,255,255,0.7);
                  border-radius:50%;
                  display:flex;align-items:center;justify-content:center;
                  color:#fff;font-size:11px;font-weight:700;
                  box-shadow:0 2px 8px rgba(59,130,246,0.65);
                ">${count}</div>`,
                iconSize: [32, 32],
                iconAnchor: [16, 16],
              });
            }}
          >
            {renderOtherMarkers()}
          </MarkerClusterGroup>
        ) : (
          renderOtherMarkers()
        )}
      </MapContainer>
    </div>
  );
}
